/**
 * The automations manager. A single dialog whose "screens" are swapped in place
 * (no modal-on-modal): the rule list, the builder, the run-now count and the
 * delete confirmation, each with its own footer.
 *
 * Draft state lives here (not in the fields) because `FormDialog` only extracts
 * Body/Footer from its own direct children.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  BookOpenIcon,
  PlusIcon,
  SparklesIcon,
  TriangleAlertIcon,
  WalletIcon,
  ZapIcon,
} from 'lucide-react';

import {
  trackerAiCapabilitiesQueryOptions,
  trackerAutomationListQueryOptions,
  useAcceptTrackerAutomationSuggestionMutation,
  useCreateTrackerAutomationMutation,
  useDeleteTrackerAutomationMutation,
  useMoveTrackerAutomationMutation,
  useRunTrackerAutomationMutation,
  useUpdateTrackerAutomationMutation,
  type AutomationAction,
  type AutomationConditions,
  type AutomationTrigger,
  type TrackerAutomation,
} from '@/entities/tracker-automation';
import { useHasWorkspacePermission } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { FormDialog } from '@/shared/ui/FormDialog';
import { EmptyState } from '@/shared/ui';
import { notificationService } from '@/lib/services/notification.service';
import { useSortableList } from '@/shared/hooks';
import { track } from '@/shared/analytics';

import type { CustomColumn, StatusOption } from '../../lib/useTrackersBoard';
import {
  authorTimeZone,
  blankAction,
  blankTrigger,
  brokenReference,
  ruleInteractions,
  ruleProblem,
  summarize,
  type TriggerColumn,
} from '../../lib/automation-model';
import type { TrackerBoardType } from '../../lib/trackers-analytics';
import { useFilterOptionLabels } from '../../lib/useFilterOptionLabels';
import { automationErrorKey } from '../../lib/automation-errors';
import { useRefreshBoardRows } from '../../lib/useRefreshBoardRows';
import { AutomationAiBudget } from './AutomationAiBudget';
import { AutomationDescribeRule } from './AutomationDescribeRule';
import { AutomationSuggestions } from './AutomationSuggestions';
import { AutomationRuleFields } from './AutomationRuleFields';
import { AutomationRuleRow } from './AutomationRuleRow';
import { AutomationPreview } from './AutomationPreview';
import { AutomationRecipeGallery } from './AutomationRecipeGallery';
import { recipesFor, type RecipeId } from '../../lib/automation-recipes';
import { AutomationRunList } from './AutomationRunList';
import { NewAutomationSplitButton } from './NewAutomationSplitButton';
import { automationsListState } from './listState';
import { useTranslation } from 'react-i18next';

interface AutomationsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trackerId: string;
  boardType: TrackerBoardType;
  columns: CustomColumn[];
  /**
   * What a rule can watch: the board's columns plus, on a record board, the
   * record's own fields. Actions still only write `columns`.
   */
  triggerColumns: TriggerColumn[];
  customGroups: StatusOption[];
}

export function AutomationsPanel({
  open,
  onOpenChange,
  trackerId,
  boardType,
  columns,
  triggerColumns,
  customGroups,
}: AutomationsPanelProps) {
  const { t } = useTranslation('trackers');
  const labels = useFilterOptionLabels();

  const {
    data: list,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...trackerAutomationListQueryOptions(trackerId),
    enabled: open && Boolean(trackerId),
  });
  const automations = useMemo(() => list?.items ?? [], [list]);
  const workspaceId = useCurrentWorkspaceId();
  const { data: aiCapabilities } = useQuery({
    ...trackerAiCapabilitiesQueryOptions(workspaceId),
    enabled: open && Boolean(workspaceId),
  });
  // Unknown until the list lands; the server still refuses what it must.
  const canManage = list?.canManage ?? true;
  const atLimit =
    list !== undefined &&
    (list.usage.boardCount >= list.usage.boardLimit ||
      list.usage.workspaceCount >= list.usage.workspaceLimit);
  const limitReason = !list
    ? undefined
    : list.usage.boardCount >= list.usage.boardLimit
      ? t('trackersBoard.automationsPanel.boardLimitReached', { limit: list.usage.boardLimit })
      : t('trackersBoard.automationsPanel.workspaceLimitReached', {
          limit: list.usage.workspaceLimit,
        });
  const createMutation = useCreateTrackerAutomationMutation({ trackerId });
  const updateMutation = useUpdateTrackerAutomationMutation({ trackerId });
  const deleteMutation = useDeleteTrackerAutomationMutation({ trackerId });
  const countMutation = useRunTrackerAutomationMutation({ trackerId });
  const refreshRows = useRefreshBoardRows(trackerId, boardType);
  const runMutation = useRunTrackerAutomationMutation({ trackerId, refreshRows });
  const moveMutation = useMoveTrackerAutomationMutation({ trackerId });
  const acceptSuggestion = useAcceptTrackerAutomationSuggestionMutation({ trackerId });

  // The AI budget belongs to the selected workspace, so only its admins see it.
  const isAdmin = useHasWorkspacePermission();

  const [view, setView] = useState<
    'list' | 'builder' | 'run' | 'delete' | 'history' | 'recipes' | 'budget' | 'describe'
  >('list');
  const [viewingHistory, setViewingHistory] = useState<TrackerAutomation | null>(null);
  const [editing, setEditing] = useState<TrackerAutomation | null>(null);
  /** Set while the builder holds a copy of a rule: where it goes and that it starts paused. */
  const [copyOf, setCopyOf] = useState<{ position: number; timeZone?: string } | null>(null);
  /** The suggestion the builder was opened from, taken only once its rule is created. */
  const [fromSuggestion, setFromSuggestion] = useState<string | null>(null);
  const [running, setRunning] = useState<TrackerAutomation | null>(null);
  const [deleting, setDeleting] = useState<TrackerAutomation | null>(null);
  const [name, setName] = useState('');
  const [conditions, setConditions] = useState<AutomationConditions | null>(null);
  const [trigger, setTrigger] = useState<AutomationTrigger>(() =>
    blankTrigger(triggerColumns, boardType),
  );
  const [actions, setActions] = useState<AutomationAction[]>(() => [
    blankAction(columns, blankTrigger(triggerColumns, boardType)),
  ]);

  // Always land on the list when the dialog (re)opens.
  useEffect(() => {
    if (open) setView('list');
  }, [open]);

  const openNew = () => {
    const next = blankTrigger(triggerColumns, boardType);
    setEditing(null);
    setCopyOf(null);
    setFromSuggestion(null);
    setName('');
    setTrigger(next);
    setActions([blankAction(columns, next)]);
    setConditions(null);
    setView('builder');
  };
  const openRecipe = (
    rule: { trigger: AutomationTrigger; actions: AutomationAction[] },
    recipeName: string,
    recipeId: RecipeId,
  ) => {
    setEditing(null);
    setCopyOf(null);
    setFromSuggestion(null);
    setName(recipeName);
    setTrigger(rule.trigger);
    setActions(rule.actions);
    setConditions(null);
    setView('builder');
    track('tracker_automation_recipe_picked', {
      tracker_id: trackerId,
      board_type: boardType,
      recipe_id: recipeId,
    });
  };
  const recipes = useMemo(
    () => recipesFor(boardType, { columns, triggerColumns, groups: customGroups }),
    [boardType, columns, triggerColumns, customGroups],
  );
  const openEdit = (rule: TrackerAutomation) => {
    setEditing(rule);
    setCopyOf(null);
    setFromSuggestion(null);
    setName(rule.name ?? '');
    setTrigger(rule.trigger);
    setActions(rule.actions.length ? rule.actions : [blankAction(columns, rule.trigger)]);
    setConditions(rule.conditions ?? null);
    setView('builder');
  };
  // The count is asked for before anything runs, so the person sees how many
  // rows will change and can back out.
  const openRun = (rule: TrackerAutomation) => {
    setRunning(rule);
    countMutation.reset();
    countMutation.mutate({ automationId: rule.id, dryRun: true });
    setView('run');
  };
  const confirmRun = () => {
    if (!running) return;
    const triggerType = running.trigger.type;
    runMutation.mutate(
      { automationId: running.id, dryRun: false },
      {
        onSuccess: ({ rowCount: counted, queuedCount }) => {
          track('tracker_automation_run_now', {
            tracker_id: trackerId,
            board_type: boardType,
            trigger_type: triggerType,
            row_count: queuedCount,
          });
          // Rows the queue refused are left as they were; running again picks them up.
          if (queuedCount < counted) {
            notificationService.showWarning(
              t('trackersBoard.automationsPanel.runPartlyQueued', {
                queued: queuedCount,
                count: counted,
              }),
            );
          } else {
            notificationService.showSuccess(
              t('trackersBoard.automationsPanel.runQueued', { count: queuedCount }),
            );
          }
        },
        onError: (error) =>
          notificationService.showError(
            t(automationErrorKey(error, 'trackersBoard.automationsPanel.runFailed')),
          ),
      },
    );
    setView('list');
  };
  // The copy starts paused, so two identical rules never both fire before the
  // person has changed one of them. A webhook or Slack URL is never read back
  // and belongs to its rule, so a rule posting to one is copied through the
  // builder, where the person pastes the URL again.
  const duplicate = (rule: TrackerAutomation, index: number) => {
    if (rule.actions.some((a) => a.type === 'webhook' || a.type === 'slack')) {
      setEditing(null);
      setFromSuggestion(null);
      setCopyOf({ position: index + 1, timeZone: rule.timeZone ?? undefined });
      setName(t('trackersBoard.automationsPanel.copyName', { name: rule.name ?? summaryOf(rule) }));
      setTrigger(rule.trigger);
      setActions(
        rule.actions.map((a) =>
          a.type === 'webhook' || a.type === 'slack'
            ? { ...a, url: '', urlKey: undefined, urlPreview: undefined }
            : a,
        ),
      );
      setConditions(rule.conditions ?? null);
      setView('builder');
      return;
    }
    createMutation.mutate(
      {
        name: t('trackersBoard.automationsPanel.copyName', { name: rule.name ?? summaryOf(rule) }),
        trigger: rule.trigger,
        actions: rule.actions,
        ...(rule.conditions ? { conditions: rule.conditions } : {}),
        enabled: false,
        ...(rule.timeZone ? { timeZone: rule.timeZone } : {}),
        position: index + 1,
      },
      {
        onSuccess: () =>
          track('tracker_automation_duplicated', {
            tracker_id: trackerId,
            board_type: boardType,
            trigger_type: rule.trigger.type,
          }),
        onError: (error) =>
          notificationService.showError(
            t(automationErrorKey(error, 'trackersBoard.automationErrors.duplicateFailed')),
          ),
      },
    );
  };
  const confirmDelete = () => {
    if (!deleting) return;
    deleteMutation.mutate(
      { automationId: deleting.id },
      {
        onSuccess: () => {
          track('tracker_automation_deleted', { tracker_id: trackerId, board_type: boardType });
          setView('list');
        },
        onError: (error) =>
          notificationService.showError(
            t(automationErrorKey(error, 'trackersBoard.automationsPanel.deleteFailed')),
          ),
      },
    );
  };

  const ruleIds = useMemo(() => automations.map((rule) => rule.id), [automations]);
  const { sensors, handleDragEnd } = useSortableList(ruleIds, ({ oldIndex, newIndex }) => {
    moveMutation.mutate(
      { automationId: ruleIds[oldIndex], toIndex: newIndex },
      {
        onError: (error) =>
          notificationService.showError(
            t(automationErrorKey(error, 'trackersBoard.automationsPanel.reorderFailed')),
          ),
      },
    );
    track('tracker_automation_reordered', { tracker_id: trackerId, board_type: boardType });
  });

  // A saved rule's hours are read in its own zone, a rule saved without one in UTC.
  const summaryOf = (
    rule: Pick<TrackerAutomation, 'trigger' | 'actions' | 'conditions' | 'timeZone'>,
  ) =>
    summarize(
      rule.trigger,
      rule.actions,
      triggerColumns,
      customGroups,
      rule.conditions,
      labels,
      rule.timeZone ?? 'UTC',
    );
  const brokenOf = (rule: TrackerAutomation) =>
    brokenReference(rule, { columns, triggerColumns, groups: customGroups });
  const labelOf = (rule: TrackerAutomation) => rule.name ?? summaryOf(rule);
  // A copy keeps its source's clock, so it fires on the same hour and day.
  const draftTimeZone = editing
    ? (editing.timeZone ?? 'UTC')
    : copyOf
      ? (copyOf.timeZone ?? 'UTC')
      : (authorTimeZone() ?? 'UTC');
  const interactions = useMemo(
    () =>
      view === 'builder'
        ? ruleInteractions(
            { trigger, actions, timeZone: draftTimeZone },
            automations.filter((rule) => rule.id !== editing?.id),
            editing ? editing.position : Number.MAX_SAFE_INTEGER,
          )
        : null,
    [view, trigger, actions, automations, editing, draftTimeZone],
  );
  const draftReady =
    view === 'builder' && ruleProblem(trigger, actions, conditions, columns) === null;
  const previewDraft = useMemo(
    () => ({
      trigger,
      actions,
      ...(conditions ? { conditions } : {}),
      ...(draftTimeZone !== 'UTC' ? { timeZone: draftTimeZone } : {}),
    }),
    [trigger, actions, conditions, draftTimeZone],
  );
  const draftLabel = name.trim() || t('trackersBoard.automationsPanel.thisRule');
  const ruleLabel = (id: string) => {
    const rule = automations.find((r) => r.id === id);
    return rule ? labelOf(rule) : '';
  };
  const columnLabel = (id: string) => columns.find((c) => c.id === id)?.label ?? id;

  // A refused save keeps the draft, and says why it was refused.
  const saveFailed = (error: unknown) =>
    notificationService.showError(
      t(
        automationErrorKey(
          error,
          copyOf
            ? 'trackersBoard.automationErrors.duplicateFailed'
            : 'trackersBoard.automationsPanel.saveFailed',
        ),
      ),
      { persist: true },
    );

  const submit = () => {
    if (editing) {
      updateMutation.mutate(
        {
          automationId: editing.id,
          patch: { name, trigger, actions, conditions, timeZone: authorTimeZone() },
        },
        {
          onError: saveFailed,
          onSuccess: () => {
            track('tracker_automation_updated', {
              tracker_id: trackerId,
              board_type: boardType,
              trigger_type: trigger.type,
              action_count: actions.length,
            });
            setView('list');
          },
        },
      );
      return;
    }
    createMutation.mutate(
      {
        ...(name.trim() ? { name } : {}),
        trigger,
        actions,
        ...(conditions ? { conditions } : {}),
        enabled: !copyOf,
        ...(copyOf ? { position: copyOf.position } : {}),
        // A date rule fires on its author's day, read in this zone.
        ...(copyOf
          ? copyOf.timeZone
            ? { timeZone: copyOf.timeZone }
            : {}
          : { timeZone: authorTimeZone() }),
      },
      {
        onError: saveFailed,
        onSuccess: () => {
          if (copyOf) {
            track('tracker_automation_duplicated', {
              tracker_id: trackerId,
              board_type: boardType,
              trigger_type: trigger.type,
            });
            setView('list');
            return;
          }
          if (fromSuggestion) acceptSuggestion.mutate({ suggestionId: fromSuggestion });
          track('tracker_automation_created', {
            tracker_id: trackerId,
            board_type: boardType,
            trigger_type: trigger.type,
            action_types: actions.map((a) => a.type),
            action_count: actions.length,
            condition_count: conditions?.rules.length ?? 0,
          });
          setView('list');
        },
      },
    );
  };

  const listState = automationsListState({ isLoading, isError, ruleCount: automations.length });

  const title =
    view === 'list'
      ? t('trackersBoard.automationsPanel.automations')
      : view === 'run'
        ? t('trackersBoard.automationsPanel.runOnMatchingRows')
        : view === 'recipes'
          ? t('trackersBoard.automationRecipes.recipesTitle')
          : view === 'budget'
            ? t('trackersBoard.automationAi.budget.title')
            : view === 'describe'
              ? t('trackersBoard.automationAi.describe.title')
              : view === 'delete'
                ? t('trackersBoard.automationsPanel.deleteAutomation')
                : view === 'history'
                  ? t('trackersBoard.automationRuns.history')
                  : editing
                    ? t('trackersBoard.automationsPanel.editAutomation')
                    : t('trackersBoard.automationsPanel.newAutomation');
  const rowCount = countMutation.data?.rowCount ?? 0;
  const runBroken = running ? brokenOf(running) : null;
  const runBlocked = runBroken !== null;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      // Size and height cap are the dialog's own defaults, which are already
      // the 640px the design asks for. `scrollable` keeps the header and the
      // footer pinned while the rules scroll.
      scrollable
      // For an admin Radix would focus the first control, the AI budget icon,
      // and its tooltip would open with the dialog. The dialog itself takes
      // focus instead; Tab still reaches every control.
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        (event.currentTarget as HTMLElement | null)?.focus();
      }}
    >
      <FormDialog.Body>
        {view === 'list' ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {t('trackersBoard.automationsPanel.runActionsAutomaticallyWhenThis')}
            </p>
            {/* The empty state owns the create buttons; every other state that
                is not still loading keeps them here, so a board whose rules
                cannot be read is not also read-only. */}
            {(isAdmin ||
              (canManage && aiCapabilities?.modelsAvailable && listState !== 'loading') ||
              (canManage && (listState === 'rules' || listState === 'error'))) && (
              <div className="flex items-center gap-2">
                {isAdmin ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon-sm"
                          aria-label={t('trackersBoard.automationAi.budget.open')}
                          onClick={() => setView('budget')}
                        >
                          <WalletIcon aria-hidden />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('trackersBoard.automationAi.budget.open')}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
                <span className="flex-1" />
                {canManage && (listState === 'rules' || listState === 'error') ? (
                  <NewAutomationSplitButton
                    disabled={atLimit}
                    disabledReason={atLimit ? limitReason : undefined}
                    onBlank={openNew}
                    onDescribe={
                      aiCapabilities?.modelsAvailable ? () => setView('describe') : undefined
                    }
                    onRecipes={() => setView('recipes')}
                  />
                ) : canManage && aiCapabilities?.modelsAvailable && listState === 'empty' ? (
                  // The reason sits on a wrapper: a disabled Tessra Button
                  // takes no pointer events, so its own `title` never shows.
                  <span title={atLimit ? limitReason : undefined}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setView('describe')}
                      disabled={atLimit}
                    >
                      <SparklesIcon aria-hidden />
                      {t('trackersBoard.automationAi.describe.open')}
                    </Button>
                  </span>
                ) : null}
              </div>
            )}

            {listState === 'loading' && (
              // Shaped to AutomationRuleRow, down to the corner tab, the padding
              // and the switch and menu button, so rows do not jump when the
              // data lands.
              <div className="flex flex-col gap-2" aria-hidden>
                {['s1', 's2', 's3'].map((key) => (
                  <div
                    key={key}
                    className="relative flex items-start gap-3 rounded-lg border border-border px-3 pt-5 pb-3"
                  >
                    <Skeleton className="absolute top-0 left-0 h-4 w-5 rounded-none rounded-tl-lg rounded-br-md" />
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Skeleton className="h-4 w-1/2 rounded-sm" />
                      <Skeleton className="h-3 w-3/4 rounded-sm" />
                      <Skeleton className="h-3 w-1/3 rounded-sm" />
                    </div>
                    <Skeleton className="h-5 w-9 shrink-0 rounded-full" />
                    <Skeleton className="size-6 shrink-0 rounded-md" />
                  </div>
                ))}
              </div>
            )}

            {listState === 'error' && (
              <EmptyState
                role="alert"
                variant="outline-dashed"
                // Repeated at `md` because the design system doubles its
                // padding there, and this card is inside a 640px dialog at
                // every breakpoint, so the roomier treatment never fits.
                className="py-8 md:py-8"
                icon={<TriangleAlertIcon aria-hidden />}
                title={t('trackersBoard.automationsPanel.automationsFailedToLoad')}
                descriptionMaxWidth="28rem"
                description={t('trackersBoard.automationsPanel.somethingWentWrongLoadingThis')}
              >
                <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
                  {t('trackersBoard.automationsPanel.tryAgain')}
                </Button>
              </EmptyState>
            )}

            {listState === 'empty' && (
              <EmptyState
                variant="outline-dashed"
                className="py-8 md:py-8"
                icon={<ZapIcon aria-hidden />}
                title={t('trackersBoard.automationsPanel.noAutomationsYet')}
                descriptionMaxWidth="28rem"
                description={t('trackersBoard.automationsPanel.createARuleToReact')}
              >
                {canManage ? (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setView('recipes')}
                    >
                      <BookOpenIcon aria-hidden />
                      {t('trackersBoard.automationRecipes.browse')}
                    </Button>
                    <Button type="button" size="sm" onClick={openNew}>
                      <PlusIcon aria-hidden />
                      {t('trackersBoard.automationsPanel.newAutomation')}
                    </Button>
                  </div>
                ) : null}
              </EmptyState>
            )}

            {canManage && listState !== 'loading' && listState !== 'error' && !atLimit ? (
              <AutomationSuggestions
                trackerId={trackerId}
                summarize={(suggestion) =>
                  summaryOf({ ...suggestion, conditions: null, timeZone: null })
                }
                onReview={(suggestion) => {
                  setEditing(null);
                  setCopyOf(null);
                  setFromSuggestion(suggestion.id);
                  setName('');
                  setTrigger(suggestion.trigger);
                  setActions(suggestion.actions);
                  setConditions(null);
                  setView('builder');
                }}
              />
            ) : null}

            {!canManage && listState !== 'loading' ? (
              <p className="text-xs text-muted-foreground">
                {t('trackersBoard.automationsPanel.onlyOwnerOrAdminManages')}
              </p>
            ) : null}

            {listState === 'rules' && (
              <>
                <p className="text-xs text-muted-foreground">
                  {t('trackersBoard.automationsPanel.orderDecidesConflicts')}
                </p>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={ruleIds} strategy={verticalListSortingStrategy}>
                    <ul className="flex flex-col gap-2">
                      {automations.map((rule, index) => (
                        <AutomationRuleRow
                          key={rule.id}
                          rule={rule}
                          order={index + 1}
                          onHistory={() => {
                            setViewingHistory(rule);
                            setView('history');
                          }}
                          summary={summaryOf(rule)}
                          broken={brokenOf(rule)}
                          onRun={
                            // A decision is an event no row is already sitting in, so there is nothing to run.
                            canManage && rule.enabled && rule.trigger.type !== 'approval_event'
                              ? () => openRun(rule)
                              : undefined
                          }
                          manage={
                            canManage
                              ? {
                                  onEnabledChange: (enabled) => {
                                    updateMutation.mutate(
                                      { automationId: rule.id, patch: { enabled } },
                                      {
                                        onError: (error) =>
                                          notificationService.showError(
                                            t(
                                              automationErrorKey(
                                                error,
                                                'trackersBoard.automationsPanel.saveFailed',
                                              ),
                                            ),
                                          ),
                                      },
                                    );
                                    track('tracker_automation_enabled_toggled', {
                                      tracker_id: trackerId,
                                      board_type: boardType,
                                      enabled,
                                    });
                                  },
                                  onEdit: () => openEdit(rule),
                                  onDuplicate: atLimit ? undefined : () => duplicate(rule, index),
                                  onDelete: () => {
                                    setDeleting(rule);
                                    setView('delete');
                                  },
                                }
                              : undefined
                          }
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>
              </>
            )}
          </div>
        ) : view === 'run' && running ? (
          <div className="flex flex-col gap-3">
            <p className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground">
              {summaryOf(running)}
            </p>
            {runBlocked ? (
              <Alert icon={<TriangleAlertIcon aria-hidden />}>
                <AlertDescription>
                  {t(`trackersBoard.automationsPanel.brokenReference.${runBroken}`)}
                </AlertDescription>
              </Alert>
            ) : (
              <p className="text-sm text-foreground" role="status">
                {countMutation.isPending
                  ? t('trackersBoard.automationsPanel.countingRows')
                  : countMutation.isError
                    ? t('trackersBoard.automationsPanel.countFailed')
                    : rowCount > 0
                      ? t('trackersBoard.automationsPanel.willChangeRows', { count: rowCount })
                      : t('trackersBoard.automationsPanel.noRowsToChange')}
              </p>
            )}
            {countMutation.isError && !runBlocked ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => openRun(running)}
              >
                {t('trackersBoard.automationsPanel.tryAgain')}
              </Button>
            ) : null}
          </div>
        ) : view === 'budget' ? (
          <AutomationAiBudget />
        ) : view === 'describe' ? (
          <AutomationDescribeRule
            trackerId={trackerId}
            summarize={(proposal) => summaryOf({ ...proposal, timeZone: draftTimeZone })}
            onOpenInBuilder={(proposal) => {
              setEditing(null);
              setCopyOf(null);
              setFromSuggestion(null);
              setName(proposal.name ?? '');
              setTrigger(proposal.trigger);
              setActions(proposal.actions);
              setConditions(proposal.conditions ?? null);
              setView('builder');
            }}
          />
        ) : view === 'recipes' ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {t('trackersBoard.automationRecipes.intro')}
            </p>
            <AutomationRecipeGallery recipes={recipes} onPick={openRecipe} />
          </div>
        ) : view === 'history' && viewingHistory ? (
          <div className="flex flex-col gap-3">
            <p className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground">
              {labelOf(viewingHistory)}
            </p>
            <AutomationRunList
              trackerId={trackerId}
              filter={{ automationId: viewingHistory.id }}
              columns={columns}
              groups={customGroups}
            />
          </div>
        ) : view === 'delete' && deleting ? (
          <div className="flex flex-col gap-3">
            <p className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground">
              {deleting.name ?? summaryOf(deleting)}
            </p>
            <p className="text-sm text-foreground">
              {t('trackersBoard.automationsPanel.deleteCannotBeUndone')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="automation-name">{t('trackersBoard.automationsPanel.name')}</Label>
              <Input
                id="automation-name"
                value={name}
                maxLength={120}
                placeholder={t('trackersBoard.automationsPanel.namePlaceholder')}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <AutomationRuleFields
              trigger={trigger}
              actions={actions}
              conditions={conditions}
              timeZone={draftTimeZone}
              onTriggerChange={setTrigger}
              onActionsChange={setActions}
              onConditionsChange={setConditions}
              columns={columns}
              triggerColumns={triggerColumns}
              customGroups={customGroups}
              boardType={boardType}
              webhookSecret={editing?.webhookSecret}
              trackerId={trackerId}
              aiAvailable={aiCapabilities?.modelsAvailable ?? false}
              agentsAvailable={aiCapabilities?.agentsAvailable ?? false}
            />
            {aiCapabilities?.budgetReached &&
            actions.some((a) => a.type === 'ai_fill' || a.type === 'run_agent') ? (
              <Alert>
                <TriangleAlertIcon aria-hidden />
                <AlertDescription>
                  {t('trackersBoard.automationAi.budget.reachedWarning')}
                </AlertDescription>
              </Alert>
            ) : null}
            {interactions?.loop.loops ? (
              <Alert icon={<TriangleAlertIcon aria-hidden />}>
                <AlertDescription>
                  {interactions.loop.through.length === 0
                    ? t('trackersBoard.automationsPanel.selfLoopWarning', { rule: draftLabel })
                    : t('trackersBoard.automationsPanel.loopWarning', {
                        rule: draftLabel,
                        others: interactions.loop.through
                          .map(ruleLabel)
                          .join(t('trackersBoard.automationsPanel.loopArrow')),
                      })}
                </AlertDescription>
              </Alert>
            ) : null}
            {draftReady && canManage ? (
              <AutomationPreview
                trackerId={trackerId}
                draft={previewDraft}
                columns={columns}
                groups={customGroups}
              />
            ) : null}
            {interactions?.conflicts.map((conflict) => (
              <Alert
                key={`${conflict.ruleId}-${conflict.columnId}`}
                icon={<TriangleAlertIcon aria-hidden />}
              >
                <AlertDescription>
                  {t(
                    conflict.draftWins
                      ? 'trackersBoard.automationsPanel.conflictDraftWins'
                      : 'trackersBoard.automationsPanel.conflictOtherWins',
                    {
                      rule: draftLabel,
                      other: ruleLabel(conflict.ruleId),
                      column: columnLabel(conflict.columnId),
                    },
                  )}
                </AlertDescription>
              </Alert>
            ))}
          </div>
        )}
      </FormDialog.Body>

      {(view === 'history' || view === 'recipes' || view === 'budget' || view === 'describe') && (
        <FormDialog.Footer
          secondary={{
            label: t('trackersBoard.automationsPanel.back'),
            onClick: () => setView('list'),
          }}
        />
      )}

      {view === 'delete' && (
        <FormDialog.Footer
          secondary={{
            label: t('trackersBoard.automationsPanel.back'),
            onClick: () => setView('list'),
          }}
          primary={{
            label: t('trackersBoard.automationsPanel.deleteAutomation'),
            onClick: confirmDelete,
            loading: deleteMutation.isPending,
          }}
        />
      )}

      {view === 'run' && (
        <FormDialog.Footer
          secondary={{
            label: t('trackersBoard.automationsPanel.back'),
            onClick: () => setView('list'),
          }}
          primary={{
            label: t('trackersBoard.automationsPanel.runOnRows', { count: rowCount }),
            onClick: confirmRun,
            disabled: runBlocked || countMutation.isPending || rowCount === 0,
          }}
        />
      )}

      {view === 'builder' && (
        <FormDialog.Footer
          secondary={{
            label: t('trackersBoard.automationsPanel.back'),
            onClick: () => setView('list'),
          }}
          primary={{
            label: editing
              ? t('trackersBoard.automationsPanel.saveChanges')
              : t('trackersBoard.automationsPanel.createAutomation'),
            onClick: submit,
            disabled: ruleProblem(trigger, actions, conditions, columns) !== null,
            // The edit toggle shares updateMutation, so only this view's own save locks it.
            loading: editing ? updateMutation.isPending : createMutation.isPending,
          }}
        />
      )}
    </FormDialog>
  );
}
